import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { getReportBuilder, getDownloadFileName } from "../../reports";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #525659;
`;

const PdfWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  background: #525659;
  overflow: hidden;
  position: relative;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #f1f5f9;
  text-align: center;
  padding-top: 24px;
  font-weight: 500;
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

export default function PdfPreview({
  title = "Documento",
  reportName,
  loadData,
  meta,
  loadingText = "Generando PDF...",
  className,
  style,
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("documento.pdf");

  const lastUrlRef = useRef(null);
  const runIdRef = useRef(0);
  const iframeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const runId = ++runIdRef.current;

    const run = async () => {
      if (!reportName || typeof loadData !== "function") return;

      const builder = getReportBuilder(reportName);
      if (!builder) {
        toast.error("No existe plantilla para este reporte");
        return;
      }

      setLoading(true);

      try {
        const data = await loadData();
        const blob = builder({ data, meta });
        if (!(blob instanceof Blob)) throw new Error("No se pudo generar el PDF");

        // Construir nombre de archivo: tipo+numero+-+nombrepaciente.pdf
        const computedName = blob.filename || getDownloadFileName(data?.plan || data) || "reporte.pdf";
        setFileName(computedName);

        // Crear File con el nombre exacto asignado
        const file = new File([blob], computedName, { type: "application/pdf" });
        const rawUrl = URL.createObjectURL(file);
        // Adjuntar hash con el nombre de archivo para ayudar al visor nativo de Chromium
        const url = `${rawUrl}#filename=${encodeURIComponent(computedName)}`;

        if (cancelled || runIdRef.current !== runId) {
          URL.revokeObjectURL(rawUrl);
          return;
        }

        if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
        lastUrlRef.current = rawUrl;
        setPdfUrl(url);
      } catch (e) {
        const msg = typeof e?.message === "string" && e.message.trim() ? e.message : "Error generando el PDF";
        toast.error(msg);
      } finally {
        if (!cancelled && runIdRef.current === runId) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [reportName, loadData, meta]);

  return (
    <Wrapper className={className} style={style}>
      <PdfWrapper>
        {!pdfUrl ? (
          <LoadingText>{loading ? loadingText : "Cargando visor..."}</LoadingText>
        ) : (
          <PdfFrame ref={iframeRef} title={fileName} src={pdfUrl} />
        )}
      </PdfWrapper>
    </Wrapper>
  );
}
